import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import "../styles/Admin.css";

const API_BASE = process.env.NODE_ENV === "production"
    ? "https://main-api.academy-polyglot.site/api/admin"
    : "http://localhost:5001/api/admin";
const IMG_BASE = process.env.NODE_ENV === "production"
    ? "https://main-api.academy-polyglot.site"
    : "http://localhost:5001";

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function imgUrl(p) {
    if (!p) return "";
    if (p.startsWith("http")) return p;
    return IMG_BASE + (p.startsWith("/") ? "" : "/") + p;
}
function fmtDate(d) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtAMD(n) {
    if (!n && n !== 0) return "—";
    return Number(n).toLocaleString() + " ֏";
}
function daysUntil(dateStr) {
    if (!dateStr) return null;
    return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

// ─── API HOOK ─────────────────────────────────────────────────────────────────
function useApi(token) {
    return useCallback(async (method, path, body, isFormData = false) => {
        const headers = { "x-admin-token": token };
        if (!isFormData) headers["Content-Type"] = "application/json";
        const opts = { method, headers };
        if (body !== undefined) opts.body = isFormData ? body : JSON.stringify(body);
        const r = await fetch(API_BASE + path, opts);
        if (r.status === 401) throw new Error("__UNAUTHORIZED__");
        const text = await r.text();
        try { return JSON.parse(text); } catch { return text; }
    }, [token]);
}

// ─── TOAST ────────────────────────────────────────────────────────────────────
function useToast() {
    const [toasts, setToasts] = useState([]);
    const push = useCallback((msg, type = "ok") => {
        const id = Date.now() + Math.random();
        setToasts(p => [...p, { id, msg, type }]);
        setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3800);
    }, []);
    return { toasts, push };
}
function ToastContainer({ toasts }) {
    return (
        <div className="adm-toast-wrap">
            {toasts.map(t => (
                <div key={t.id} className={`adm-toast adm-toast-${t.type}`}>
                    <span>{t.type === "ok" ? "✅" : "❌"}</span>
                    <span>{t.msg}</span>
                </div>
            ))}
        </div>
    );
}

// ─── MODAL ────────────────────────────────────────────────────────────────────
function Modal({ title, onClose, onSave, saving, wide, children }) {
    useEffect(() => {
        const h = e => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [onClose]);
    return (
        <div className="adm-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className={`adm-modal${wide ? " adm-modal-wide" : ""}`}>
                <div className="adm-modal-head">
                    <span className="adm-modal-title">{title}</span>
                    <button className="adm-modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="adm-modal-body">{children}</div>
                {onSave && (
                    <div className="adm-modal-foot">
                        <button className="adm-btn adm-btn-ghost" onClick={onClose}>Cancel</button>
                        <button className="adm-btn adm-btn-primary" onClick={onSave} disabled={saving}>
                            {saving ? <span className="adm-spinner" /> : "Save"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── LANG TABS ────────────────────────────────────────────────────────────────
const LANGS = [
    { code: "hy", flag: "🇦🇲", label: "HY" },
    { code: "en", flag: "🇬🇧", label: "EN" },
    { code: "ru", flag: "🇷🇺", label: "RU" },
    { code: "ka", flag: "🇬🇪", label: "KA" },
];
function LangTabs({ active, onChange }) {
    return (
        <div className="adm-lang-tabs">
            {LANGS.map(l => (
                <button key={l.code}
                    className={`adm-lang-tab${active === l.code ? " active" : ""}`}
                    onClick={() => onChange(l.code)}>
                    {l.flag} {l.label}
                </button>
            ))}
        </div>
    );
}

// ─── IMG UPLOAD ───────────────────────────────────────────────────────────────
function ImgField({ label, value, onChange, folder, token }) {
    const fileRef = useRef();
    const [uploading, setUploading] = useState(false);
    async function handleFile(e) {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("image", file);
            const r = await fetch(`${API_BASE}/upload?folder=${folder}`, {
                method: "POST", headers: { "x-admin-token": token }, body: form
            });
            const j = await r.json();
            if (j.url) onChange(j.url);
        } finally { setUploading(false); }
    }
    return (
        <div className="adm-f-group adm-full">
            <label className="adm-f-label">{label}</label>
            <div className="adm-img-row">
                <input className="adm-f-input" value={value || ""} onChange={e => onChange(e.target.value)} placeholder="/picture/..." />
                <button className="adm-btn adm-btn-ghost adm-btn-sm" type="button"
                    onClick={() => fileRef.current.click()} disabled={uploading}>
                    {uploading ? <span className="adm-spinner" /> : "📁 Upload"}
                </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
            {value && <img className="adm-img-preview" src={imgUrl(value)} alt="" onError={e => e.target.style.display = "none"} />}
        </div>
    );
}

// ─── MULTILANG FIELDS ────────────────────────────────────────────────────────
function MultiLangFields({ fields, form, onChange, activeLang }) {
    return (
        <>
            {fields.map(f => (
                <div key={`${f.name}_${activeLang}`} className={`adm-f-group${f.full ? " adm-full" : ""}`}>
                    <label className="adm-f-label">{f.label} <span className="adm-f-lang">{activeLang}</span></label>
                    {f.textarea
                        ? <textarea className="adm-f-textarea" rows={3}
                            value={form[`${f.name}_${activeLang}`] || ""}
                            onChange={e => onChange(`${f.name}_${activeLang}`, e.target.value)} />
                        : <input className="adm-f-input"
                            value={form[`${f.name}_${activeLang}`] || ""}
                            onChange={e => onChange(`${f.name}_${activeLang}`, e.target.value)} />
                    }
                </div>
            ))}
        </>
    );
}

// ─── DATA TABLE ───────────────────────────────────────────────────────────────
function DataTable({ id, cols, rows, toolbar, loading, statusFilter, onStatusFilter, statusOptions }) {
    const [q, setQ] = useState("");
    const filtered = useMemo(() => {
        if (!q) return rows;
        return rows.filter(r => Object.values(r).join(" ").toLowerCase().includes(q.toLowerCase()));
    }, [rows, q]);
    return (
        <div className="adm-table-wrap">
            <div className="adm-table-toolbar">
                <input className="adm-search" placeholder={`Search…`} value={q} onChange={e => setQ(e.target.value)} />
                {statusOptions && (
                    <select className="adm-f-select adm-filter-select" value={statusFilter || ""} onChange={e => onStatusFilter(e.target.value)}>
                        {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                )}
                {toolbar}
            </div>
            {loading ? (
                <div className="adm-loading"><span className="adm-spinner" /> Loading…</div>
            ) : (
                <div className="adm-table-scroll">
                    <table className="adm-data-table">
                        <thead><tr>{cols.map(c => <th key={c}>{c}</th>)}</tr></thead>
                        <tbody>
                            {filtered.length === 0
                                ? <tr><td colSpan={cols.length} className="adm-empty-cell">No records found</td></tr>
                                : filtered.map((r, i) => <tr key={r.__key || i}>{r.__cells}</tr>)}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
    const map = { completed: "adm-tag-green", pending: "adm-tag-yellow", failed: "adm-tag-red",
                  cancelled: "adm-tag-red", active: "adm-tag-green", expired: "adm-tag-red",
                  hidden: "adm-tag-red", refunded: "adm-tag-blue" };
    return <span className={`adm-tag ${map[status] || "adm-tag-blue"}`}>{status || "—"}</span>;
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: DASHBOARD
// ═══════════════════════════════════════════════════════════════════
function Dashboard({ api }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api("GET", "/dashboard").then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    }, [api]);

    if (loading) return <div className="adm-loading"><span className="adm-spinner" /> Loading…</div>;
    if (!data) return <div className="adm-empty-cell">Failed to load dashboard</div>;

    const { counts, revenue, recentOrders, paymentBreakdown } = data;
    const statCards = [
        { icon: "💰", val: fmtAMD(revenue?.total_revenue), label: "Total Revenue", accent: true },
        { icon: "✅", val: revenue?.completed_orders || 0, label: "Completed Orders" },
        { icon: "⏳", val: revenue?.pending_orders || 0, label: "Pending Orders" },
        { icon: "❌", val: revenue?.failed_orders || 0, label: "Failed Orders" },
        { icon: "🌐", val: counts.products, label: "Products" },
        { icon: "🎓", val: counts.certificates, label: "Certificates" },
        { icon: "📱", val: counts.telegramActive, label: "Active VIP Subs" },
        { icon: "📧", val: counts.subscribers, label: "Subscribers" },
        { icon: "📩", val: counts.contacts, label: "Messages" },
        { icon: "🤝", val: counts.partners || 0, label: "Partners" },
    ];

    return (
        <div>
            <div className="adm-stats-grid">
                {statCards.map(s => (
                    <div key={s.label} className={`adm-stat-card${s.accent ? " adm-stat-accent" : ""}`}>
                        <div className="adm-stat-icon">{s.icon}</div>
                        <div className="adm-stat-val">{s.val}</div>
                        <div className="adm-stat-label">{s.label}</div>
                    </div>
                ))}
            </div>
            {paymentBreakdown?.length > 0 && (
                <div className="adm-table-wrap" style={{ marginTop: 20 }}>
                    <div className="adm-table-toolbar"><strong className="adm-section-title">Revenue by Payment Method</strong></div>
                    <table className="adm-data-table">
                        <thead><tr><th>Provider</th><th>Total</th><th>Completed</th><th>Revenue</th><th>Conv %</th></tr></thead>
                        <tbody>
                            {paymentBreakdown.map(p => (
                                <tr key={p.payment_type}>
                                    <td><span className="adm-tag adm-tag-blue">{p.payment_type || "—"}</span></td>
                                    <td>{p.total}</td><td>{p.completed}</td>
                                    <td>{fmtAMD(p.revenue)}</td>
                                    <td><span className={`adm-tag ${p.total > 0 && p.completed / p.total > 0.5 ? "adm-tag-green" : "adm-tag-yellow"}`}>
                                        {p.total > 0 ? Math.round(p.completed / p.total * 100) : 0}%
                                    </span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {recentOrders?.length > 0 && (
                <div className="adm-table-wrap" style={{ marginTop: 20 }}>
                    <div className="adm-table-toolbar"><strong className="adm-section-title">Recent Orders</strong></div>
                    <table className="adm-data-table">
                        <thead><tr><th>ID</th><th>Customer</th><th>Phone</th><th>Amount</th><th>Payment</th><th>Status</th><th>Date</th></tr></thead>
                        <tbody>
                            {recentOrders.map(o => (
                                <tr key={o.id}>
                                    <td><span className="adm-tag adm-tag-blue">#{o.id}</span></td>
                                    <td>{o.customer_name} {o.customer_surname || ""}</td>
                                    <td>{o.customer_phone}</td>
                                    <td>{fmtAMD(o.total_amount)}</td>
                                    <td><span className="adm-tag adm-tag-blue">{o.payment_type || "—"}</span></td>
                                    <td><StatusBadge status={o.status} /></td>
                                    <td>{fmtDate(o.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: PRODUCTS (full pricing + SEO + gallery + sort)
// ═══════════════════════════════════════════════════════════════════
const TARIFF_GROUPS = [
    {
        label: "Standard",
        tariffs: [
            { key: "individual",  label: "Individual",        fullCol: "individual_full_price",  monthlyCol: "individual_monthly_price" },
            { key: "group2",      label: "Group (2 people)",  fullCol: "group2_full_price",       monthlyCol: "group2_monthly_price" },
            { key: "group5",      label: "Group (5 people)",  fullCol: "group5_full_price",       monthlyCol: "group5_monthly_price" },
            { key: "speaking",    label: "Speaking",          fullCol: "speaking_full_price",     monthlyCol: "speaking_monthly_price" },
            { key: "accelerated", label: "Accelerated",       fullCol: "accelerated_full_price",  monthlyCol: "accelerated_monthly_price" },
            { key: "hybrid",      label: "Hybrid",            fullCol: "hybrid_full_price",       monthlyCol: "hybrid_monthly_price" },
        ]
    },
    {
        label: "Native Speaker",
        tariffs: [
            { key: "native_ind",     label: "Native Individual", fullCol: "native_individual_price", monthlyCol: null },
            { key: "native_group",   label: "Native Group",      fullCol: "native_group_price",      monthlyCol: null },
            { key: "native_speaking",label: "Native Speaking",   fullCol: "native_speaking_price",   monthlyCol: null },
        ]
    },
    {
        label: "Kids",
        tariffs: [
            { key: "kids_ind", label: "Kids Individual", fullCol: "kids_individual_price", monthlyCol: null },
        ]
    },
    {
        label: "IELTS EASY",
        tariffs: [
            { key: "ielts_easy_g", label: "IELTS EASY Group",      fullCol: "ielts_easy_group_full",       monthlyCol: "ielts_easy_group_monthly" },
            { key: "ielts_easy_i", label: "IELTS EASY Individual", fullCol: "ielts_easy_individual_full",  monthlyCol: "ielts_easy_individual_monthly" },
        ]
    },
    {
        label: "IELTS ADVANCED",
        tariffs: [
            { key: "ielts_adv_g", label: "IELTS ADV Group",      fullCol: "ielts_advanced_group_full",       monthlyCol: "ielts_advanced_group_monthly" },
            { key: "ielts_adv_i", label: "IELTS ADV Individual", fullCol: "ielts_advanced_individual_full",  monthlyCol: "ielts_advanced_individual_monthly" },
        ]
    },
    {
        label: "Armenian Pairs",
        tariffs: [
            { key: "eng_arm", label: "English-Armenian", fullCol: "english_armenian_full", monthlyCol: null },
            { key: "rus_arm", label: "Russian-Armenian", fullCol: "russian_armenian_full", monthlyCol: null },
        ]
    },
];

function Products({ api, token, toast }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [activeLang, setActiveLang] = useState("hy");
    const [activeTab, setActiveTab] = useState("content"); // content | pricing | seo | gallery

    const load = useCallback(async () => {
        setLoading(true);
        try { setData(await api("GET", "/products")); } finally { setLoading(false); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    function setF(k, v) { setForm(p => ({ ...p, [k]: v })); }

    async function save() {
        if (!form["name_hy"] && !form["name_en"]) { toast("Product name (HY or EN) is required", "err"); return; }
        setSaving(true);
        try {
            if (form.id) await api("PUT", `/products/${form.id}`, form);
            else await api("POST", "/products", form);
            toast("Product saved!", "ok"); setModal(null); load();
        } catch { toast("Save failed", "err"); } finally { setSaving(false); }
    }

    async function del(id) {
        if (!window.confirm(`Delete product #${id}?`)) return;
        await api("DELETE", `/products/${id}`);
        toast("Deleted", "ok"); load();
    }

    async function toggleStatus(p) {
        await api("PUT", `/products/${p.id}`, { status: p.status == 1 ? 0 : 1 });
        toast("Status updated", "ok"); load();
    }

    const rows = data.map(p => ({
        __key: p.id,
        __cells: (
            <>
                <td><span className="adm-tag adm-tag-blue">{p.id}</span></td>
                <td>{p.img ? <img className="adm-thumb" src={imgUrl(p.img)} alt="" onError={e => e.target.style.display = "none"} /> : <span className="adm-thumb-ph">🌐</span>}</td>
                <td style={{ fontWeight: 500 }}>{p.name_hy || p.name_en || "—"}</td>
                <td>{p.name_en || "—"}</td>
                <td><span className="adm-tag adm-tag-yellow">{p.price ? fmtAMD(p.price) : "—"}</span></td>
                <td>
                    <button className={`adm-tag ${p.status == 1 ? "adm-tag-green" : "adm-tag-red"}`}
                        style={{ background: "none", border: "none", cursor: "pointer" }}
                        onClick={() => toggleStatus(p)}>
                        {p.status == 1 ? "Active" : "Hidden"}
                    </button>
                </td>
                <td>
                    <div className="adm-actions">
                        <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { setForm({ ...p }); setActiveLang("hy"); setActiveTab("content"); setModal(true); }}>✏️ Edit</button>
                        <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => del(p.id)}>🗑</button>
                    </div>
                </td>
            </>
        ),
    }));

    const PRODUCT_TABS = ["content", "pricing", "seo", "gallery"];

    return (
        <>
            <DataTable id="products" cols={["ID", "Image", "Name (HY)", "Name (EN)", "Price", "Status", "Actions"]}
                rows={rows} loading={loading}
                toolbar={<button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => { setForm({ status: 1, sort_order: 0 }); setActiveLang("hy"); setActiveTab("content"); setModal(true); }}>+ Add Product</button>}
            />
            {modal && (
                <Modal title={form.id ? `Edit Product #${form.id}` : "New Product"} onClose={() => setModal(null)} onSave={save} saving={saving} wide>
                    {/* Tab bar */}
                    <div className="adm-product-tabs">
                        {PRODUCT_TABS.map(tab => (
                            <button key={tab} className={`adm-product-tab${activeTab === tab ? " active" : ""}`} onClick={() => setActiveTab(tab)}>
                                {{ content: "📝 Content", pricing: "💰 Pricing", seo: "🔍 SEO", gallery: "🖼 Gallery" }[tab]}
                            </button>
                        ))}
                    </div>

                    {/* CONTENT TAB */}
                    {activeTab === "content" && (
                        <div>
                            <LangTabs active={activeLang} onChange={setActiveLang} />
                            <div className="adm-form-grid">
                                <MultiLangFields fields={[
                                    { name: "name", label: "Course Name" },
                                    { name: "description", label: "Description", textarea: true, full: true },
                                ]} form={form} onChange={setF} activeLang={activeLang} />
                                <div className="adm-f-group">
                                    <label className="adm-f-label">Base Price (AMD)</label>
                                    <input className="adm-f-input" type="number" value={form.price || ""} onChange={e => setF("price", e.target.value)} />
                                </div>
                                <div className="adm-f-group">
                                    <label className="adm-f-label">Status</label>
                                    <select className="adm-f-select" value={form.status ?? 1} onChange={e => setF("status", e.target.value)}>
                                        <option value={1}>Active</option>
                                        <option value={0}>Hidden</option>
                                    </select>
                                </div>
                                <div className="adm-f-group">
                                    <label className="adm-f-label">Sort Order</label>
                                    <input className="adm-f-input" type="number" value={form.sort_order || 0} onChange={e => setF("sort_order", e.target.value)} />
                                </div>
                                <ImgField label="Cover Image" value={form.img} onChange={v => setF("img", v)} folder="product" token={token} />
                            </div>
                        </div>
                    )}

                    {/* PRICING TAB */}
                    {activeTab === "pricing" && (
                        <div>
                            <p style={{ fontSize: 13, color: "var(--adm-text2)", marginBottom: 14 }}>
                                Leave a price field empty (0 or blank) to hide that tariff from the course page.
                            </p>
                            {TARIFF_GROUPS.map(group => (
                                <div key={group.label} style={{ marginBottom: 20 }}>
                                    <div className="adm-trans-sec-head">{group.label}</div>
                                    <div className="adm-form-grid" style={{ marginTop: 10 }}>
                                        {group.tariffs.map(t => (
                                            <React.Fragment key={t.key}>
                                                <div className="adm-f-group">
                                                    <label className="adm-f-label">{t.label} — Full</label>
                                                    <input className="adm-f-input" type="number" placeholder="0"
                                                        value={form[t.fullCol] || ""}
                                                        onChange={e => setF(t.fullCol, e.target.value)} />
                                                </div>
                                                {t.monthlyCol && (
                                                    <div className="adm-f-group">
                                                        <label className="adm-f-label">{t.label} — Monthly</label>
                                                        <input className="adm-f-input" type="number" placeholder="0"
                                                            value={form[t.monthlyCol] || ""}
                                                            onChange={e => setF(t.monthlyCol, e.target.value)} />
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* SEO TAB */}
                    {activeTab === "seo" && (
                        <div>
                            <LangTabs active={activeLang} onChange={setActiveLang} />
                            <div className="adm-form-grid" style={{ marginTop: 12 }}>
                                <div className="adm-f-group adm-full">
                                    <label className="adm-f-label">Meta Title <span className="adm-f-lang">{activeLang}</span></label>
                                    <input className="adm-f-input" value={form[`seo_title_${activeLang}`] || ""} onChange={e => setF(`seo_title_${activeLang}`, e.target.value)} placeholder="Course Name | Polyglot Academy" />
                                </div>
                                <div className="adm-f-group adm-full">
                                    <label className="adm-f-label">Meta Description <span className="adm-f-lang">{activeLang}</span></label>
                                    <textarea className="adm-f-textarea" rows={2} value={form[`seo_desc_${activeLang}`] || ""} onChange={e => setF(`seo_desc_${activeLang}`, e.target.value)} placeholder="150 characters recommended" />
                                </div>
                                <div className="adm-f-group adm-full">
                                    <label className="adm-f-label">Keywords (comma separated)</label>
                                    <input className="adm-f-input" value={form.seo_keywords || ""} onChange={e => setF("seo_keywords", e.target.value)} placeholder="english course, polyglot, yerevan" />
                                </div>
                                <ImgField label="OG Image (1200×630 recommended)" value={form.og_image} onChange={v => setF("og_image", v)} folder="seo" token={token} />
                            </div>
                        </div>
                    )}

                    {/* GALLERY TAB */}
                    {activeTab === "gallery" && (
                        <div>
                            <p style={{ fontSize: 13, color: "var(--adm-text2)", marginBottom: 12 }}>
                                Paste image URLs, one per line. These appear in the course page gallery.
                            </p>
                            <textarea className="adm-f-textarea" rows={8}
                                value={(() => { try { return (JSON.parse(form.img_gallery || "[]")).join("\n"); } catch { return form.img_gallery || ""; } })()}
                                onChange={e => {
                                    const lines = e.target.value.split("\n").map(l => l.trim()).filter(Boolean);
                                    setF("img_gallery", JSON.stringify(lines));
                                }}
                                placeholder="/picture/product/img1.jpg&#10;/picture/product/img2.jpg" />
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                                {(() => { try { return JSON.parse(form.img_gallery || "[]"); } catch { return []; } })().map((url, i) => (
                                    <img key={i} src={imgUrl(url)} alt="" style={{ width: 80, height: 60, objectFit: "cover", borderRadius: 6, border: "1px solid var(--adm-border)" }} onError={e => e.target.style.display = "none"} />
                                ))}
                            </div>
                        </div>
                    )}
                </Modal>
            )}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: SITE SETTINGS CMS
// ═══════════════════════════════════════════════════════════════════
const SETTINGS_GROUPS = [
    { key: "contact",    label: "📞 Contact Info",      desc: "Phone, email, address, Google Maps link" },
    { key: "social",     label: "🔗 Social Links",      desc: "Facebook, Instagram, Telegram, LinkedIn, WhatsApp, VK, Messenger" },
    { key: "statistics", label: "📊 Statistics Numbers", desc: "Students, graduates, team members, beneficiaries" },
    { key: "polia",      label: "🤖 Polia Bot",         desc: "Bot URL, level test URL" },
    { key: "course",     label: "📚 Course Page",       desc: "CTA button URL, FAQ image" },
    { key: "footer",     label: "🦶 Footer Links",      desc: "Quick links in the footer" },
    { key: "navbar",     label: "🧭 Navbar",            desc: "Logo image URL" },
    { key: "about",      label: "👥 About Section",     desc: "Images for the About and About Us page" },
    { key: "faq",        label: "❓ FAQ Section",       desc: "FAQ section image" },
    { key: "why",        label: "✅ Why Choose Section", desc: "Why choose section image" },
];

function SiteSettings({ api, token, toast }) {
    const [activeGroup, setActiveGroup] = useState("contact");
    const [settings, setSettings] = useState({ flat: [], grouped: {} });
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api("GET", `/settings?group=${activeGroup}`);
            setSettings(data);
            // Flatten to form: key_lang -> value
            const f = {};
            for (const row of data.flat || []) {
                f[`${row.key}__${row.lang}`] = row.value || "";
            }
            setForm(f);
        } finally { setLoading(false); }
    }, [api, activeGroup]);
    useEffect(() => { load(); }, [load]);

    async function save() {
        setSaving(true);
        try {
            // Build array of updates
            const updates = [];
            for (const [compKey, value] of Object.entries(form)) {
                const lastDouble = compKey.lastIndexOf("__");
                const key = compKey.substring(0, lastDouble);
                const lang = compKey.substring(lastDouble + 2);
                updates.push({ key, lang, value, group_name: activeGroup });
            }
            await api("PUT", "/settings", updates);
            toast("Settings saved!", "ok");
        } catch { toast("Save failed", "err"); } finally { setSaving(false); }
    }

    // Build rows for current group based on loaded flat data
    const groupRows = useMemo(() => {
        const seen = new Set();
        return (settings.flat || [])
            .filter(r => { if (seen.has(r.key)) return false; seen.add(r.key); return true; })
            .map(r => ({ key: r.key, label: r.label || r.key, lang: r.lang }));
    }, [settings]);

    function renderField(key, lang, label, isImage = false) {
        const formKey = `${key}__${lang}`;
        const value = form[formKey] || "";
        const isUrl = key.includes("url") || key.includes("link") || key.includes("facebook") ||
                      key.includes("instagram") || key.includes("telegram") || key.includes("linkedin") ||
                      key.includes("whatsapp") || key.includes("messenger") || key.includes("vk");

        return (
            <div key={formKey} className="adm-setting-row">
                <div className="adm-setting-label">
                    <span className="adm-f-label">{label}</span>
                    <span className="adm-f-lang">{lang}</span>
                </div>
                {isImage ? (
                    <div>
                        <div className="adm-img-row">
                            <input className="adm-f-input" value={value} onChange={e => setForm(p => ({ ...p, [formKey]: e.target.value }))} />
                        </div>
                        {value && <img src={imgUrl(value)} alt="" className="adm-img-preview" onError={e => e.target.style.display = "none"} />}
                    </div>
                ) : (
                    <input className={`adm-f-input${isUrl ? " adm-mono" : ""}`}
                        value={value}
                        onChange={e => setForm(p => ({ ...p, [formKey]: e.target.value }))}
                        placeholder={isUrl ? "https://..." : ""}
                    />
                )}
            </div>
        );
    }

    return (
        <div style={{ display: "flex", gap: 16 }}>
            {/* Left nav */}
            <div className="adm-settings-nav">
                {SETTINGS_GROUPS.map(g => (
                    <div key={g.key}
                        className={`adm-settings-nav-item${activeGroup === g.key ? " active" : ""}`}
                        onClick={() => setActiveGroup(g.key)}>
                        {g.label}
                    </div>
                ))}
            </div>

            {/* Right content */}
            <div style={{ flex: 1 }}>
                <div className="adm-table-wrap">
                    <div className="adm-table-toolbar">
                        <strong className="adm-section-title">
                            {SETTINGS_GROUPS.find(g => g.key === activeGroup)?.label}
                        </strong>
                        <span style={{ fontSize: 12, color: "var(--adm-text3)", flex: 1 }}>
                            {SETTINGS_GROUPS.find(g => g.key === activeGroup)?.desc}
                        </span>
                        <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={save} disabled={saving}>
                            {saving ? <span className="adm-spinner" /> : "💾 Save"}
                        </button>
                    </div>
                    {loading ? <div className="adm-loading"><span className="adm-spinner" /> Loading…</div> : (
                        <div style={{ padding: "12px 16px" }}>
                            {groupRows.length === 0 && <div className="adm-empty-cell">No settings found for this group</div>}
                            {groupRows.map(({ key, label, lang }) => {
                                const isImg = key.includes("image") || key.includes("img") || key.includes("logo");
                                return renderField(key, lang, label, isImg);
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: PARTNERS
// ═══════════════════════════════════════════════════════════════════
function Partners({ api, token, toast }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { setData(await api("GET", "/partners")); } finally { setLoading(false); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    function setF(k, v) { setForm(p => ({ ...p, [k]: v })); }
    async function save() {
        if (!form.name || !form.logo_url) { toast("Name and logo URL are required", "err"); return; }
        setSaving(true);
        try {
            if (form.id) await api("PUT", `/partners/${form.id}`, form);
            else await api("POST", "/partners", form);
            toast("Partner saved!", "ok"); setModal(null); load();
        } catch { toast("Save failed", "err"); } finally { setSaving(false); }
    }
    async function del(id) {
        if (!window.confirm(`Delete partner #${id}?`)) return;
        await api("DELETE", `/partners/${id}`);
        toast("Deleted", "ok"); load();
    }

    const rows = data.map(p => ({
        __key: p.id,
        __cells: (
            <>
                <td><span className="adm-tag adm-tag-blue">{p.id}</span></td>
                <td><img className="adm-thumb" src={imgUrl(p.logo_url)} alt={p.name} onError={e => e.target.style.display = "none"} /></td>
                <td style={{ fontWeight: 500 }}>{p.name}</td>
                <td className="adm-truncate" style={{ maxWidth: 200, fontSize: 12 }}>{p.link_url || "—"}</td>
                <td>{p.sort_order}</td>
                <td><StatusBadge status={p.status == 1 ? "active" : "hidden"} /></td>
                <td>
                    <div className="adm-actions">
                        <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { setForm({ ...p }); setModal(true); }}>✏️</button>
                        <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => del(p.id)}>🗑</button>
                    </div>
                </td>
            </>
        ),
    }));

    return (
        <>
            <DataTable id="partners" cols={["ID", "Logo", "Name", "Link", "Order", "Status", "Actions"]}
                rows={rows} loading={loading}
                toolbar={<button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => { setForm({ status: 1, sort_order: data.length }); setModal(true); }}>+ Add Partner</button>}
            />
            {modal && (
                <Modal title={form.id ? "Edit Partner" : "New Partner"} onClose={() => setModal(null)} onSave={save} saving={saving}>
                    <div className="adm-form-grid">
                        <div className="adm-f-group adm-full">
                            <label className="adm-f-label">Partner Name <span className="adm-required">*</span></label>
                            <input className="adm-f-input" value={form.name || ""} onChange={e => setF("name", e.target.value)} />
                        </div>
                        <ImgField label="Logo Image *" value={form.logo_url} onChange={v => setF("logo_url", v)} folder="partners" token={token} />
                        <div className="adm-f-group adm-full">
                            <label className="adm-f-label">Partner Website URL</label>
                            <input className="adm-f-input adm-mono" value={form.link_url || ""} onChange={e => setF("link_url", e.target.value)} placeholder="https://..." />
                        </div>
                        <div className="adm-f-group">
                            <label className="adm-f-label">Sort Order</label>
                            <input className="adm-f-input" type="number" value={form.sort_order || 0} onChange={e => setF("sort_order", e.target.value)} />
                        </div>
                        <div className="adm-f-group">
                            <label className="adm-f-label">Status</label>
                            <select className="adm-f-select" value={form.status ?? 1} onChange={e => setF("status", e.target.value)}>
                                <option value={1}>Visible</option>
                                <option value={0}>Hidden</option>
                            </select>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: SEO MANAGEMENT
// ═══════════════════════════════════════════════════════════════════
const SEO_PAGES = [
    { key: "home",        label: "🏠 Home Page" },
    { key: "about",       label: "👥 About Us Page" },
    { key: "contact",     label: "📞 Contact Page" },
    { key: "products",    label: "🌐 Products / Courses Page" },
    { key: "certificate", label: "🎓 Certificate Page" },
];

function SEOManagement({ api, toast }) {
    const [activePage, setActivePage] = useState("home");
    const [activeLang, setActiveLang] = useState("hy");
    const [allSeo, setAllSeo] = useState([]);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api("GET", "/seo").then(rows => setAllSeo(rows));
    }, [api]);

    useEffect(() => {
        const row = allSeo.find(r => r.page_key === activePage && r.lang === activeLang);
        setForm(row ? { ...row } : { page_key: activePage, lang: activeLang });
    }, [activePage, activeLang, allSeo]);

    function setF(k, v) { setForm(p => ({ ...p, [k]: v })); }

    async function save() {
        setSaving(true);
        try {
            await api("PUT", `/seo/${activePage}/${activeLang}`, form);
            const rows = await api("GET", "/seo");
            setAllSeo(rows);
            toast("SEO saved!", "ok");
        } catch { toast("Save failed", "err"); } finally { setSaving(false); }
    }

    return (
        <div style={{ display: "flex", gap: 16 }}>
            <div className="adm-settings-nav">
                {SEO_PAGES.map(p => (
                    <div key={p.key}
                        className={`adm-settings-nav-item${activePage === p.key ? " active" : ""}`}
                        onClick={() => setActivePage(p.key)}>
                        {p.label}
                    </div>
                ))}
            </div>
            <div style={{ flex: 1 }}>
                <div className="adm-table-wrap">
                    <div className="adm-table-toolbar">
                        <strong className="adm-section-title">{SEO_PAGES.find(p => p.key === activePage)?.label} — SEO</strong>
                        <LangTabs active={activeLang} onChange={setActiveLang} />
                        <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={save} disabled={saving}>
                            {saving ? <span className="adm-spinner" /> : "💾 Save"}
                        </button>
                    </div>
                    <div style={{ padding: "16px" }}>
                        <div className="adm-form-grid">
                            <div className="adm-f-group adm-full">
                                <label className="adm-f-label">Meta Title <span className="adm-f-lang">{activeLang}</span>
                                    <span style={{ fontWeight: 400, marginLeft: 8, color: "var(--adm-text3)" }}>({(form.meta_title || "").length}/60)</span>
                                </label>
                                <input className="adm-f-input" value={form.meta_title || ""} onChange={e => setF("meta_title", e.target.value)} placeholder="Page Title | Polyglot Academy" />
                            </div>
                            <div className="adm-f-group adm-full">
                                <label className="adm-f-label">Meta Description
                                    <span style={{ fontWeight: 400, marginLeft: 8, color: "var(--adm-text3)" }}>({(form.meta_desc || "").length}/160)</span>
                                </label>
                                <textarea className="adm-f-textarea" rows={3} value={form.meta_desc || ""} onChange={e => setF("meta_desc", e.target.value)} placeholder="Describe this page in 150-160 characters" />
                            </div>
                            <div className="adm-f-group adm-full">
                                <label className="adm-f-label">Keywords</label>
                                <input className="adm-f-input" value={form.keywords || ""} onChange={e => setF("keywords", e.target.value)} placeholder="polyglot, language course, english, yerevan" />
                            </div>
                            <div className="adm-f-group adm-full">
                                <div className="adm-trans-sec-head" style={{ marginBottom: 12 }}>Open Graph (Social Preview)</div>
                            </div>
                            <div className="adm-f-group adm-full">
                                <label className="adm-f-label">OG Title</label>
                                <input className="adm-f-input" value={form.og_title || ""} onChange={e => setF("og_title", e.target.value)} placeholder="Defaults to Meta Title if empty" />
                            </div>
                            <div className="adm-f-group adm-full">
                                <label className="adm-f-label">OG Description</label>
                                <textarea className="adm-f-textarea" rows={2} value={form.og_desc || ""} onChange={e => setF("og_desc", e.target.value)} placeholder="Defaults to Meta Description if empty" />
                            </div>
                            <div className="adm-f-group adm-full">
                                <label className="adm-f-label">OG Image URL (1200×630)</label>
                                <input className="adm-f-input adm-mono" value={form.og_image || ""} onChange={e => setF("og_image", e.target.value)} placeholder="/picture/og/home.jpg" />
                                {form.og_image && <img src={imgUrl(form.og_image)} alt="" className="adm-img-preview" style={{ maxWidth: 240 }} onError={e => e.target.style.display = "none"} />}
                            </div>
                            <div className="adm-f-group adm-full">
                                <label className="adm-f-label">Canonical URL</label>
                                <input className="adm-f-input adm-mono" value={form.canonical_url || ""} onChange={e => setF("canonical_url", e.target.value)} placeholder="https://polyglotacademy.am/hy/..." />
                            </div>
                        </div>

                        {/* Preview */}
                        {(form.meta_title || form.meta_desc) && (
                            <div style={{ marginTop: 20 }}>
                                <div className="adm-trans-sec-head">Google Preview</div>
                                <div style={{ background: "var(--adm-bg)", border: "1px solid var(--adm-border)", borderRadius: 8, padding: 14, marginTop: 10 }}>
                                    <div style={{ color: "#1a0dab", fontSize: 18, fontWeight: 400, lineHeight: 1.3 }}>{form.meta_title || "Page Title"}</div>
                                    <div style={{ color: "#006621", fontSize: 13, marginTop: 4 }}>polyglotacademy.am › {activeLang} › {activePage}</div>
                                    <div style={{ color: "#545454", fontSize: 14, marginTop: 4, lineHeight: 1.5 }}>{form.meta_desc || "Page description appears here…"}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: COMMENTS
// ═══════════════════════════════════════════════════════════════════
function Comments({ api, token, toast }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const load = useCallback(async () => { setLoading(true); try { setData(await api("GET", "/comments")); } finally { setLoading(false); } }, [api]);
    useEffect(() => { load(); }, [load]);
    function setF(k, v) { setForm(p => ({ ...p, [k]: v })); }
    async function save() {
        setSaving(true);
        try {
            if (form.id) await api("PUT", `/comments/${form.id}`, form);
            else await api("POST", "/comments", { ...form, text: form.text || form.comment });
            toast("Saved!", "ok"); setModal(null); load();
        } catch { toast("Save failed", "err"); } finally { setSaving(false); }
    }
    async function del(id) {
        if (!window.confirm(`Delete?`)) return;
        await api("DELETE", `/comments/${id}`); toast("Deleted", "ok"); load();
    }
    const rows = data.map(c => ({
        __key: c.id,
        __cells: (
            <>
                <td><span className="adm-tag adm-tag-blue">{c.id}</span></td>
                <td>{c.img ? <img className="adm-thumb" src={imgUrl(c.img)} alt="" onError={e => e.target.style.display = "none"} /> : <span className="adm-thumb-ph">👤</span>}</td>
                <td>{c.name}</td>
                <td className="adm-truncate" style={{ maxWidth: 260 }}>{c.text || c.comment}</td>
                <td>{"⭐".repeat(Math.min(c.stars || 5, 5))}</td>
                <td><span className="adm-tag adm-tag-blue">{c.lang || "—"}</span></td>
                <td><div className="adm-actions">
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { setForm({ ...c }); setModal(true); }}>✏️</button>
                    <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => del(c.id)}>🗑</button>
                </div></td>
            </>
        ),
    }));
    return (
        <>
            <DataTable id="reviews" cols={["ID","Photo","Name","Text","Stars","Lang","Actions"]} rows={rows} loading={loading}
                toolbar={<button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => { setForm({ stars: 5, lang: "hy" }); setModal(true); }}>+ Add Review</button>} />
            {modal && (
                <Modal title={form.id ? "Edit Review" : "New Review"} onClose={() => setModal(null)} onSave={save} saving={saving}>
                    <div className="adm-form-grid">
                        <div className="adm-f-group"><label className="adm-f-label">Name</label><input className="adm-f-input" value={form.name||""} onChange={e=>setF("name",e.target.value)}/></div>
                        <div className="adm-f-group"><label className="adm-f-label">Stars</label><input className="adm-f-input" type="number" min={1} max={5} value={form.stars||5} onChange={e=>setF("stars",e.target.value)}/></div>
                        <div className="adm-f-group adm-full"><label className="adm-f-label">Review Text</label><textarea className="adm-f-textarea" rows={3} value={form.text||form.comment||""} onChange={e=>setF("text",e.target.value)}/></div>
                        <div className="adm-f-group"><label className="adm-f-label">Language</label>
                            <select className="adm-f-select" value={form.lang||"hy"} onChange={e=>setF("lang",e.target.value)}>
                                {LANGS.map(l=><option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
                            </select></div>
                        <ImgField label="Photo" value={form.img} onChange={v=>setF("img",v)} folder="comment" token={token} />
                    </div>
                </Modal>
            )}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: TEACHERS
// ═══════════════════════════════════════════════════════════════════
function Teachers({ api, token, toast }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [activeLang, setActiveLang] = useState("hy");
    const load = useCallback(async () => { setLoading(true); try { setData(await api("GET", "/teachers")); } finally { setLoading(false); } }, [api]);
    useEffect(() => { load(); }, [load]);
    function setF(k, v) { setForm(p => ({ ...p, [k]: v })); }
    async function save() {
        setSaving(true);
        try {
            if (form.id) await api("PUT", `/teachers/${form.id}`, form);
            else await api("POST", "/teachers", form);
            toast("Saved!", "ok"); setModal(null); load();
        } catch { toast("Save failed", "err"); } finally { setSaving(false); }
    }
    async function del(id) { if (!window.confirm("Delete?")) return; await api("DELETE", `/teachers/${id}`); toast("Deleted","ok"); load(); }
    const rows = data.map(t => ({
        __key: t.id,
        __cells: (
            <>
                <td><span className="adm-tag adm-tag-blue">{t.id}</span></td>
                <td>{t.img ? <img className="adm-thumb" src={imgUrl(t.img)} alt="" onError={e=>e.target.style.display="none"}/> : <span className="adm-thumb-ph">👩‍🏫</span>}</td>
                <td>{t.name_hy}</td><td>{t.name_en}</td><td>{t.role}</td>
                <td><div className="adm-actions">
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={()=>{setForm({...t});setActiveLang("hy");setModal(true);}}>✏️</button>
                    <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={()=>del(t.id)}>🗑</button>
                </div></td>
            </>
        ),
    }));
    return (
        <>
            <DataTable id="teachers" cols={["ID","Photo","Name HY","Name EN","Role","Actions"]} rows={rows} loading={loading}
                toolbar={<button className="adm-btn adm-btn-primary adm-btn-sm" onClick={()=>{setForm({});setActiveLang("hy");setModal(true);}}>+ Add Teacher</button>} />
            {modal && (
                <Modal title={form.id?"Edit Teacher":"New Teacher"} onClose={()=>setModal(null)} onSave={save} saving={saving} wide>
                    <LangTabs active={activeLang} onChange={setActiveLang} />
                    <div className="adm-form-grid">
                        <MultiLangFields fields={[{name:"name",label:"Name"},{name:"description",label:"Description",textarea:true,full:true}]} form={form} onChange={setF} activeLang={activeLang} />
                        <div className="adm-f-group adm-full"><label className="adm-f-label">Role</label><input className="adm-f-input" value={form.role||""} onChange={e=>setF("role",e.target.value)}/></div>
                        <ImgField label="Photo" value={form.img} onChange={v=>setF("img",v)} folder="MyTeam" token={token} />
                    </div>
                </Modal>
            )}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: TEAM
// ═══════════════════════════════════════════════════════════════════
function Team({ api, token, toast }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [activeLang, setActiveLang] = useState("hy");
    const load = useCallback(async () => { setLoading(true); try { setData(await api("GET", "/team")); } finally { setLoading(false); } }, [api]);
    useEffect(() => { load(); }, [load]);
    function setF(k, v) { setForm(p => ({ ...p, [k]: v })); }
    async function save() {
        setSaving(true);
        try {
            if (form.id) await api("PUT", `/team/${form.id}`, form);
            else await api("POST", "/team", form);
            toast("Saved!", "ok"); setModal(null); load();
        } catch { toast("Save failed", "err"); } finally { setSaving(false); }
    }
    async function del(id) { if (!window.confirm("Delete?")) return; await api("DELETE", `/team/${id}`); toast("Deleted","ok"); load(); }
    const rows = data.map(m => ({
        __key: m.id,
        __cells: (
            <>
                <td><span className="adm-tag adm-tag-blue">{m.id}</span></td>
                <td>{m.img?<img className="adm-thumb" src={imgUrl(m.img)} alt="" onError={e=>e.target.style.display="none"}/>:<span className="adm-thumb-ph">👤</span>}</td>
                <td>{m.name_hy||m.name}</td><td>{m.role_hy||m.role}</td>
                <td><StatusBadge status={m.status==1?"active":"hidden"}/></td>
                <td><div className="adm-actions">
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={()=>{setForm({...m});setActiveLang("hy");setModal(true);}}>✏️</button>
                    <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={()=>del(m.id)}>🗑</button>
                </div></td>
            </>
        ),
    }));
    return (
        <>
            <DataTable id="team" cols={["ID","Photo","Name HY","Role HY","Status","Actions"]} rows={rows} loading={loading}
                toolbar={<button className="adm-btn adm-btn-primary adm-btn-sm" onClick={()=>{setForm({status:1});setActiveLang("hy");setModal(true);}}>+ Add Member</button>} />
            {modal && (
                <Modal title={form.id?"Edit Member":"New Member"} onClose={()=>setModal(null)} onSave={save} saving={saving} wide>
                    <LangTabs active={activeLang} onChange={setActiveLang} />
                    <div className="adm-form-grid">
                        <MultiLangFields fields={[{name:"name",label:"Name"},{name:"role",label:"Role"},{name:"description",label:"Description",textarea:true,full:true}]} form={form} onChange={setF} activeLang={activeLang} />
                        <div className="adm-f-group"><label className="adm-f-label">Status</label>
                            <select className="adm-f-select" value={form.status??1} onChange={e=>setF("status",e.target.value)}>
                                <option value={1}>Visible</option><option value={0}>Hidden</option>
                            </select></div>
                        <ImgField label="Photo" value={form.img} onChange={v=>setF("img",v)} folder="MyTeam" token={token} />
                    </div>
                </Modal>
            )}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: GALLERY
// ═══════════════════════════════════════════════════════════════════
function Gallery({ api, token, toast }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [imgVal, setImgVal] = useState("");
    const [saving, setSaving] = useState(false);
    const fileRef = useRef();
    const load = useCallback(async () => { setLoading(true); try { setData(await api("GET", "/gallery")); } finally { setLoading(false); } }, [api]);
    useEffect(() => { load(); }, [load]);
    async function handleUpload(e) {
        const file = e.target.files[0]; if (!file) return;
        const form = new FormData(); form.append("image", file);
        const r = await fetch(`${API_BASE}/upload?folder=gallery`, { method: "POST", headers: { "x-admin-token": token }, body: form });
        const j = await r.json(); if (j.url) setImgVal(j.url);
    }
    async function save() {
        if (!imgVal) { toast("No image", "err"); return; }
        setSaving(true);
        try { await api("POST", "/gallery", { img: imgVal }); toast("Added!", "ok"); setModal(false); setImgVal(""); load(); }
        catch { toast("Failed", "err"); } finally { setSaving(false); }
    }
    async function del(id) { if (!window.confirm("Delete?")) return; await api("DELETE", `/gallery/${id}`); toast("Deleted","ok"); load(); }
    return (
        <>
            <div className="adm-table-wrap">
                <div className="adm-table-toolbar">
                    <span className="adm-section-title">Gallery ({data.length} images)</span>
                    <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={()=>{setImgVal("");setModal(true);}}>+ Add Image</button>
                </div>
                {loading ? <div className="adm-loading"><span className="adm-spinner"/></div> : (
                    <div className="adm-gallery-grid">
                        {data.length === 0 && <div className="adm-empty-cell">No images</div>}
                        {data.map(g => (
                            <div key={g.id} className="adm-gallery-item">
                                <img src={imgUrl(g.img)} alt=""/>
                                <button className="adm-gallery-del" onClick={()=>del(g.id)}>🗑 Delete</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {modal && (
                <Modal title="Add Gallery Image" onClose={()=>setModal(false)} onSave={save} saving={saving}>
                    <div className="adm-form-grid">
                        <div className="adm-f-group adm-full">
                            <label className="adm-f-label">Upload File</label>
                            <button className="adm-btn adm-btn-ghost" type="button" onClick={()=>fileRef.current.click()}>📁 Choose File</button>
                            <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleUpload}/>
                        </div>
                        <div className="adm-f-group adm-full">
                            <label className="adm-f-label">Or paste URL</label>
                            <input className="adm-f-input" value={imgVal} onChange={e=>setImgVal(e.target.value)} placeholder="/picture/gallery/..."/>
                        </div>
                        {imgVal && <img src={imgUrl(imgVal)} alt="" className="adm-img-preview" style={{marginTop:8}}/>}
                    </div>
                </Modal>
            )}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: TRANSLATIONS
// ═══════════════════════════════════════════════════════════════════
function flattenObj(obj, prefix = "") {
    const res = {};
    for (const [k, v] of Object.entries(obj)) {
        const key = prefix ? `${prefix}.${k}` : k;
        if (v !== null && typeof v === "object") Object.assign(res, flattenObj(v, key));
        else res[key] = v;
    }
    return res;
}
function unflattenObj(flat) {
    const res = {};
    for (const [dotKey, val] of Object.entries(flat)) {
        const keys = dotKey.split(".");
        let cur = res;
        for (let i = 0; i < keys.length - 1; i++) {
            if (!cur[keys[i]] || typeof cur[keys[i]] !== "object") cur[keys[i]] = {};
            cur = cur[keys[i]];
        }
        cur[keys[keys.length - 1]] = val;
    }
    return res;
}
function Translations({ api, toast }) {
    const [lang, setLang] = useState("hy");
    const [flat, setFlat] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");

    const loadLang = useCallback(async (l) => {
        setLoading(true);
        try { setFlat(flattenObj(await api("GET", `/translations/${l}`))); }
        catch { toast("Failed to load", "err"); }
        finally { setLoading(false); }
    }, [api, toast]);
    useEffect(() => { loadLang(lang); }, [lang, loadLang]);

    async function save() {
        setSaving(true);
        try { await api("PUT", `/translations/${lang}`, unflattenObj(flat)); toast("Translations saved!", "ok"); }
        catch { toast("Save failed", "err"); } finally { setSaving(false); }
    }

    const sections = useMemo(() => {
        const secs = {};
        Object.entries(flat).forEach(([k, v]) => {
            if (search && !k.toLowerCase().includes(search.toLowerCase()) && !String(v).toLowerCase().includes(search.toLowerCase())) return;
            const sec = k.split(".")[0];
            if (!secs[sec]) secs[sec] = [];
            secs[sec].push([k, v]);
        });
        return secs;
    }, [flat, search]);

    return (
        <div className="adm-table-wrap">
            <div className="adm-table-toolbar">
                <LangTabs active={lang} onChange={l => { setLang(l); setSearch(""); }} />
                <input className="adm-search" placeholder="Search keys or values…" value={search} onChange={e => setSearch(e.target.value)} />
                <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={save} disabled={saving}>
                    {saving ? <span className="adm-spinner" /> : "💾 Save"}
                </button>
            </div>
            {loading ? <div className="adm-loading"><span className="adm-spinner" /> Loading translations…</div> : (
                <div className="adm-trans-content">
                    {Object.entries(sections).map(([sec, pairs]) => (
                        <div key={sec} className="adm-trans-section">
                            <div className="adm-trans-sec-head">📁 {sec}</div>
                            {pairs.map(([k, v]) => (
                                <div key={k} className="adm-trans-row">
                                    <div className="adm-trans-key" title={k}>{k}</div>
                                    {String(v).length > 80
                                        ? <textarea className="adm-f-textarea" rows={2} value={v} onChange={e => setFlat(p => ({ ...p, [k]: e.target.value }))} />
                                        : <input className="adm-f-input" value={v} onChange={e => setFlat(p => ({ ...p, [k]: e.target.value }))} />
                                    }
                                </div>
                            ))}
                        </div>
                    ))}
                    {Object.keys(sections).length === 0 && <div className="adm-empty-cell">No matches found</div>}
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: ORDERS
// ═══ ORDERS (with VPOS card payment actions) ════════════
// VPOS payment state codes per docs Table 2
const VPOS_ORDER_STATUS = {
    0: { label: 'Registered',      color: 'adm-tag-yellow' },
    1: { label: 'Pre-authorised',  color: 'adm-tag-blue'   },
    2: { label: 'Deposited',       color: 'adm-tag-green'  },
    3: { label: 'Void',            color: 'adm-tag-red'    },
    4: { label: 'Refunded',        color: 'adm-tag-blue'   },
    5: { label: 'Auto-authorised', color: 'adm-tag-green'  },
    6: { label: 'Declined',        color: 'adm-tag-red'    },
};

function VposStateBadge({ code }) {
    const st = VPOS_ORDER_STATUS[code];
    if (!st) return null;
    return <span className={`adm-tag ${st.color}`}>{st.label}</span>;
}

function Orders({ api, toast }) {
    const [data, setData]               = useState([]);
    const [loading, setLoading]         = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [detailModal, setDetailModal] = useState(null);
    const [vposDetail, setVposDetail]   = useState(null);
    const [vposLoading, setVposLoading] = useState(false);
    const [actionModal, setActionModal] = useState(null); // {type, order}
    const [actionAmount, setActionAmount] = useState('');
    const [actionSaving, setActionSaving] = useState(false);
    const [statusModal, setStatusModal] = useState(null);
    const [newStatus, setNewStatus]     = useState('');
    const [saving, setSaving]           = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const p = statusFilter ? `?status=${statusFilter}` : '';
            const res = await api('GET', `/orders${p}`);
            setData(res.rows || res);
        } finally { setLoading(false); }
    }, [api, statusFilter]);
    useEffect(() => { load(); }, [load]);

    // Fetch live VPOS details from AmeriaBank
    async function fetchVposDetails(order) {
        if (!order.payment_id) {
            toast('No payment_id recorded for this order', 'err');
            return;
        }
        setDetailModal(order);
        setVposDetail(null);
        setVposLoading(true);
        try {
            const result = await api('POST', '/orders/vpos/details', { payment_id: order.payment_id });
            setVposDetail(result);
        } catch (e) {
            setVposDetail({ error: e.message });
        } finally { setVposLoading(false); }
    }

    // Execute VPOS action: confirm / cancel / refund
    async function executeVposAction() {
        const { type, order } = actionModal;
        if ((type === 'refund' || type === 'confirm') && !actionAmount) {
            toast('Amount is required', 'err'); return;
        }
        setActionSaving(true);
        try {
            const payload = { payment_id: order.payment_id, order_id: order.id };
            if (actionAmount) payload.amount = Number(actionAmount);
            const result = await api('POST', `/orders/vpos/${type}`, payload);
            if (result.success || result.responseCode === '00') {
                toast(`${type.charAt(0).toUpperCase() + type.slice(1)} successful!`, 'ok');
                setActionModal(null);
                setDetailModal(null);
                setVposDetail(null);
                load();
            } else {
                toast(`${type} failed: ${result.responseMessage || result.error || 'Unknown error'}`, 'err');
            }
        } catch (e) {
            toast(`Error: ${e.message}`, 'err');
        } finally { setActionSaving(false); }
    }

    async function updateStatus() {
        if (!newStatus) return;
        setSaving(true);
        try {
            await api('PUT', `/orders/${statusModal.id}/status`, { status: newStatus });
            toast('Updated!', 'ok'); setStatusModal(null); load();
        } catch { toast('Failed', 'err'); } finally { setSaving(false); }
    }

    async function del(id) {
        if (!window.confirm(`Delete order #${id}?`)) return;
        await api('DELETE', `/orders/${id}`);
        toast('Deleted', 'ok'); load();
    }

    const isCardPayment = o => o.payment_type === 'card' || o.payment_type === 'ameria';

    const rows = data.map(o => {
        const hasTg = (() => { try { return !!JSON.parse(o.telegram); } catch { return !!o.telegram; } })();
        return {
            __key: o.id,
            __cells: (
                <>
                    <td><span className="adm-tag adm-tag-blue">#{o.id}</span></td>
                    <td>
                        {o.customer_name} {o.customer_surname || ''}
                        {hasTg && <span className="adm-tag adm-tag-blue" style={{marginLeft:4,fontSize:10}}>📱TG</span>}
                    </td>
                    <td>{o.customer_phone}</td>
                    <td><span className="adm-tag adm-tag-yellow">{fmtAMD(o.total_amount)}</span></td>
                    <td>
                        <span className="adm-tag adm-tag-blue">{o.payment_type || '—'}</span>
                        {isCardPayment(o) && <span className="adm-tag adm-tag-blue" style={{marginLeft:3,fontSize:10}}>💳</span>}
                    </td>
                    <td><StatusBadge status={o.status} /></td>
                    <td>{fmtDate(o.created_at)}</td>
                    <td>
                        <div className="adm-actions">
                            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => fetchVposDetails(o)} title="Details">🔍</button>
                            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { setStatusModal(o); setNewStatus(o.status); }} title="Change status">⚙️</button>
                            <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => del(o.id)} title="Delete">🗑</button>
                        </div>
                    </td>
                </>
            ),
        };
    });

    return (
        <>
            <DataTable loading={loading} rows={rows}
                cols={['ID', 'Customer', 'Phone', 'Amount', 'Payment', 'Status', 'Date', 'Actions']}
                statusFilter={statusFilter} onStatusFilter={setStatusFilter}
                statusOptions={[
                    {value:'', label:'All statuses'},
                    {value:'pending',   label:'Pending'},
                    {value:'completed', label:'Completed'},
                    {value:'failed',    label:'Failed'},
                    {value:'cancelled', label:'Cancelled'},
                    {value:'refunded',  label:'Refunded'},
                ]}
            />

            {/* ── ORDER DETAIL + VPOS ACTIONS MODAL ── */}
            {detailModal && (
                <Modal title={`Order #${detailModal.id}${isCardPayment(detailModal) ? ' — 💳 Card Payment' : ''}`}
                    onClose={() => { setDetailModal(null); setVposDetail(null); }} wide>

                    {/* Basic order info */}
                    <div className="adm-detail-grid">
                        <div className="adm-detail-row"><span>Customer</span><strong>{detailModal.customer_name} {detailModal.customer_surname}</strong></div>
                        <div className="adm-detail-row"><span>Phone</span><strong>{detailModal.customer_phone}</strong></div>
                        <div className="adm-detail-row"><span>Total</span><strong>{fmtAMD(detailModal.total_amount)}</strong></div>
                        <div className="adm-detail-row"><span>Payment Type</span><strong>{detailModal.payment_type || '—'}</strong></div>
                        <div className="adm-detail-row"><span>Payment ID</span><strong style={{fontSize:11,wordBreak:'break-all'}}>{detailModal.payment_id || '—'}</strong></div>
                        <div className="adm-detail-row"><span>Status</span><StatusBadge status={detailModal.status} /></div>
                        <div className="adm-detail-row"><span>Date</span><strong>{fmtDate(detailModal.created_at)}</strong></div>
                    </div>

                    {/* Cart items */}
                    <div style={{marginTop:14}}>
                        <div className="adm-trans-sec-head">🛒 Cart Items</div>
                        <table className="adm-data-table" style={{marginTop:8}}>
                            <thead><tr><th>Course</th><th>Type</th><th>Qty</th><th>Price</th></tr></thead>
                            <tbody>
                                {(() => { try {
                                    return JSON.parse(detailModal.cart).map((item, i) => (
                                        <tr key={i}>
                                            <td>{item.name || item.name_en || `#${item.id}`}</td>
                                            <td><span className="adm-tag adm-tag-blue" style={{fontSize:11}}>{item.selectedType || '—'}</span></td>
                                            <td>{item.quantity}</td>
                                            <td>{fmtAMD(item.price)}</td>
                                        </tr>
                                    ));
                                } catch { return <tr><td colSpan={4}>Parse error</td></tr>; }})()}
                            </tbody>
                        </table>
                    </div>

                    {/* VPOS live data from AmeriaBank */}
                    {isCardPayment(detailModal) && (
                        <div style={{marginTop:18}}>
                            <div className="adm-trans-sec-head">🏦 AmeriaBank vPOS Live Data</div>
                            {vposLoading && <div className="adm-loading" style={{padding:'16px 0'}}><span className="adm-spinner"/> Fetching from AmeriaBank…</div>}
                            {vposDetail && !vposDetail.error && (
                                <div className="adm-vpos-grid">
                                    <div className="adm-detail-grid" style={{marginTop:8}}>
                                        <div className="adm-detail-row"><span>Card Number</span><strong>{vposDetail.CardNumber || '—'}</strong></div>
                                        <div className="adm-detail-row"><span>Cardholder</span><strong>{vposDetail.ClientName || '—'}</strong></div>
                                        <div className="adm-detail-row"><span>Amount</span><strong>{fmtAMD(vposDetail.Amount)}</strong></div>
                                        <div className="adm-detail-row"><span>Approved Amount</span><strong>{fmtAMD(vposDetail.ApprovedAmount)}</strong></div>
                                        <div className="adm-detail-row"><span>Deposited Amount</span><strong>{fmtAMD(vposDetail.DepositedAmount)}</strong></div>
                                        <div className="adm-detail-row"><span>Refunded Amount</span><strong>{fmtAMD(vposDetail.RefundedAmount)}</strong></div>
                                        <div className="adm-detail-row"><span>Approval Code</span><strong className="adm-mono">{vposDetail.ApprovalCode || '—'}</strong></div>
                                        <div className="adm-detail-row"><span>RRN</span><strong className="adm-mono" style={{fontSize:11}}>{vposDetail.rrn || '—'}</strong></div>
                                        <div className="adm-detail-row"><span>Payment State</span><strong>{vposDetail.PaymentState || '—'}</strong></div>
                                        <div className="adm-detail-row">
                                            <span>Order Status</span>
                                            <span style={{display:'flex',gap:6,alignItems:'center'}}>
                                                <strong>{vposDetail.OrderStatus ?? '—'}</strong>
                                                <VposStateBadge code={Number(vposDetail.OrderStatus)} />
                                            </span>
                                        </div>
                                        <div className="adm-detail-row"><span>Response Code</span>
                                            <span className={`adm-tag ${vposDetail.ResponseCode === '00' ? 'adm-tag-green' : 'adm-tag-red'}`}>
                                                {vposDetail.ResponseCode || '—'}
                                            </span>
                                        </div>
                                        <div className="adm-detail-row"><span>Transaction Date</span><strong>{vposDetail.DateTime || '—'}</strong></div>
                                        <div className="adm-detail-row"><span>Currency</span><strong>{vposDetail.Currency || '—'}</strong></div>
                                    </div>

                                    {/* VPOS Action Buttons */}
                                    <div className="adm-vpos-actions">
                                        <div className="adm-trans-sec-head" style={{marginTop:16}}>⚡ Payment Actions</div>
                                        <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:10}}>
                                            {/* CONFIRM — for pre-authorised (OrderStatus=1) */}
                                            {Number(vposDetail.OrderStatus) === 1 && (
                                                <button className="adm-btn adm-btn-primary adm-btn-sm"
                                                    onClick={() => { setActionModal({type:'confirm', order:detailModal}); setActionAmount(String(detailModal.total_amount||'')); }}>
                                                    ✅ Confirm Payment
                                                </button>
                                            )}
                                            {/* CANCEL — for pending/pre-auth (0 or 1) */}
                                            {(Number(vposDetail.OrderStatus) === 0 || Number(vposDetail.OrderStatus) === 1) && (
                                                <button className="adm-btn adm-btn-danger adm-btn-sm"
                                                    onClick={() => { setActionModal({type:'cancel', order:detailModal}); setActionAmount(''); }}>
                                                    ✕ Cancel Payment
                                                </button>
                                            )}
                                            {/* REFUND — for deposited (2) or auto-authorised (5) */}
                                            {(Number(vposDetail.OrderStatus) === 2 || Number(vposDetail.OrderStatus) === 5) && (
                                                <button className="adm-btn adm-btn-ghost adm-btn-sm"
                                                    onClick={() => { setActionModal({type:'refund', order:detailModal}); setActionAmount(String(detailModal.total_amount||'')); }}>
                                                    ↩ Refund Payment
                                                </button>
                                            )}
                                            {/* Always allow manual refresh */}
                                            <button className="adm-btn adm-btn-ghost adm-btn-sm"
                                                onClick={() => fetchVposDetails(detailModal)}>
                                                ↺ Refresh
                                            </button>
                                        </div>
                                        <p style={{fontSize:11,color:'var(--adm-text3)',marginTop:8}}>
                                            Confirm: two-stage capture • Cancel: available within 72h • Refund: returns funds to card
                                        </p>
                                    </div>
                                </div>
                            )}
                            {vposDetail?.error && (
                                <div style={{marginTop:10,padding:'10px 12px',background:'rgba(231,76,60,0.1)',borderRadius:8,fontSize:13,color:'var(--adm-red)'}}>
                                    ❌ {vposDetail.error}
                                    {!detailModal.payment_id && <span> — No payment_id stored. Payment may not have been initiated.</span>}
                                </div>
                            )}
                            {!detailModal.payment_id && !vposLoading && (
                                <div style={{marginTop:8,fontSize:12,color:'var(--adm-text3)'}}>
                                    ⚠ No payment_id stored for this order. If the customer was redirected to the bank but payment_id was not saved, check server logs.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Telegram info */}
                    {(() => { try {
                        const tg = JSON.parse(detailModal.telegram);
                        if (!tg) return null;
                        return (
                            <div style={{marginTop:14}}>
                                <div className="adm-trans-sec-head">📱 Telegram Account</div>
                                <div className="adm-detail-grid" style={{marginTop:8}}>
                                    <div className="adm-detail-row"><span>Telegram ID</span><strong>{tg.telegram_id || tg.id}</strong></div>
                                    {tg.username && <div className="adm-detail-row"><span>Username</span><strong>@{tg.username}</strong></div>}
                                    {tg.first_name && <div className="adm-detail-row"><span>Name</span><strong>{tg.first_name}</strong></div>}
                                </div>
                            </div>
                        );
                    } catch { return null; }})()}
                </Modal>
            )}

            {/* ── VPOS ACTION CONFIRMATION MODAL ── */}
            {actionModal && (
                <Modal
                    title={{
                        confirm: '✅ Confirm Card Payment',
                        cancel:  '✕ Cancel Card Payment',
                        refund:  '↩ Refund Card Payment',
                    }[actionModal.type]}
                    onClose={() => setActionModal(null)}
                    onSave={executeVposAction}
                    saving={actionSaving}
                >
                    <div className="adm-detail-grid" style={{marginBottom:14}}>
                        <div className="adm-detail-row"><span>Order</span><strong>#{actionModal.order.id}</strong></div>
                        <div className="adm-detail-row"><span>Customer</span><strong>{actionModal.order.customer_name} {actionModal.order.customer_surname}</strong></div>
                        <div className="adm-detail-row"><span>Payment ID</span><strong className="adm-mono" style={{fontSize:11}}>{actionModal.order.payment_id}</strong></div>
                        <div className="adm-detail-row"><span>Order Total</span><strong>{fmtAMD(actionModal.order.total_amount)}</strong></div>
                    </div>

                    {(actionModal.type === 'refund' || actionModal.type === 'confirm') && (
                        <div className="adm-f-group">
                            <label className="adm-f-label">
                                Amount (AMD) <span className="adm-required">*</span>
                            </label>
                            <input className="adm-f-input" type="number" value={actionAmount}
                                onChange={e => setActionAmount(e.target.value)}
                                placeholder={String(actionModal.order.total_amount || '')} />
                            {actionModal.type === 'refund' && (
                                <span className="adm-f-hint">
                                    Partial refund: enter less than the total. Full refund: enter the full amount {fmtAMD(actionModal.order.total_amount)}.
                                </span>
                            )}
                        </div>
                    )}

                    {actionModal.type === 'cancel' && (
                        <div style={{padding:'10px 12px',background:'rgba(241,196,15,0.08)',borderRadius:8,fontSize:13,color:'var(--adm-yellow)',marginTop:8}}>
                            ⚠ Cancel is only available within 72 hours of payment registration (per AmeriaBank docs §4).
                        </div>
                    )}
                </Modal>
            )}

            {/* ── STATUS CHANGE MODAL ── */}
            {statusModal && (
                <Modal title={`Change Status — #${statusModal.id}`} onClose={() => setStatusModal(null)} onSave={updateStatus} saving={saving}>
                    <div className="adm-f-group">
                        <label className="adm-f-label">New Status</label>
                        <select className="adm-f-select" value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                            <option value="failed">Failed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="refunded">Refunded</option>
                        </select>
                    </div>
                    <div className="adm-detail-row" style={{marginTop:12}}>
                        <span>Current</span><StatusBadge status={statusModal.status} />
                    </div>
                </Modal>
            )}
        </>
    );
}


// ═══════════════════════════════════════════════════════════════════
function Newsletter({ api, toast }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => { setLoading(true); try { setData(await api("GET", "/newsletter")); } finally { setLoading(false); } }, [api]);
    useEffect(() => { load(); }, [load]);
    async function del(id) { if (!window.confirm("Remove?")) return; await api("DELETE", `/newsletter/${id}`); toast("Removed","ok"); load(); }
    function exportCSV() {
        const b = new Blob(["ID,Email,Date\n" + data.map(s=>`${s.id},${s.email},${s.created_at||""}`).join("\n")], { type: "text/csv" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "subscribers.csv"; a.click();
    }
    const rows = data.map(s => ({ __key: s.id, __cells: (<><td><span className="adm-tag adm-tag-blue">{s.id}</span></td><td>{s.email}</td><td>{fmtDate(s.created_at)}</td><td><button className="adm-btn adm-btn-danger adm-btn-sm" onClick={()=>del(s.id)}>🗑</button></td></>) }));
    return <DataTable id="subs" cols={["ID","Email","Date","Actions"]} rows={rows} loading={loading} toolbar={<button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={exportCSV}>📥 CSV</button>} />;
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: CONTACTS
// ═══════════════════════════════════════════════════════════════════
function Contacts({ api, toast }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewModal, setViewModal] = useState(null);
    const load = useCallback(async () => { setLoading(true); try { setData(await api("GET", "/contacts")); } finally { setLoading(false); } }, [api]);
    useEffect(() => { load(); }, [load]);
    async function markRead(id) { try { await api("PUT", `/contacts/${id}/read`); } catch {} setData(p => p.map(c => c.id===id?{...c,is_read:1}:c)); }
    async function del(id) { if (!window.confirm("Delete?")) return; await api("DELETE", `/contacts/${id}`); toast("Deleted","ok"); load(); }
    const rows = data.map(c => ({
        __key: c.id,
        __cells: (
            <>
                <td><span className="adm-tag adm-tag-blue">{c.id}</span>{!c.is_read&&<span className="adm-unread-dot"/>}</td>
                <td style={{fontWeight:c.is_read?400:600}}>{c.name}</td>
                <td>{c.phone}</td>
                <td className="adm-truncate" style={{maxWidth:240}}>{c.message}</td>
                <td>{fmtDate(c.created_at)}</td>
                <td><div className="adm-actions">
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={()=>{setViewModal(c);markRead(c.id);}}>👁 View</button>
                    <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={()=>del(c.id)}>🗑</button>
                </div></td>
            </>
        ),
    }));
    return (
        <>
            <DataTable id="messages" cols={["ID","Name","Phone","Message","Date","Actions"]} rows={rows} loading={loading} />
            {viewModal && (
                <Modal title={`Message from ${viewModal.name}`} onClose={()=>setViewModal(null)}>
                    <div className="adm-detail-grid">
                        <div className="adm-detail-row"><span>Name</span><strong>{viewModal.name}</strong></div>
                        <div className="adm-detail-row"><span>Phone</span><strong>{viewModal.phone}</strong></div>
                        <div className="adm-detail-row"><span>Date</span><strong>{fmtDate(viewModal.created_at)}</strong></div>
                    </div>
                    <div style={{marginTop:16}}><div className="adm-trans-sec-head">Message</div>
                    <p style={{marginTop:10,lineHeight:1.7,color:"var(--adm-text)"}}>{viewModal.message||"—"}</p></div>
                    <div style={{marginTop:16}}><a className="adm-btn adm-btn-primary" href={`tel:${viewModal.phone}`}>📞 Call</a></div>
                </Modal>
            )}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: CERTIFICATES
// ═══════════════════════════════════════════════════════════════════
const CERT_LEVELS = [
    {value:"English Level 0-A1",label:"English 0-A1"},{value:"English Level A1-A2",label:"English A1-A2"},
    {value:"English Level A2-B1",label:"English A2-B1"},{value:"English Level B1-B2",label:"English B1-B2"},
    {value:"English Level B2-C1",label:"English B2-C1"},{value:"English Level C1-C2",label:"English C1-C2"},
    {value:"Speaking club A1-A2",label:"Speaking Club A1-A2"},{value:"Speaking club B1-B2",label:"Speaking Club B1-B2"},
    {value:"Speaking club C1",label:"Speaking Club C1"},{value:"Armenian easy",label:"Armenian (easy)"},
    {value:"Russian Level 0-A1",label:"Russian 0-A1"},{value:"Russian Level A1-A2",label:"Russian A1-A2"},
    {value:"Russian Level A2-B1",label:"Russian A2-B1"},{value:"Russian Level B1-B2",label:"Russian B1-B2"},
    {value:"Russian Level B2-C1",label:"Russian B2-C1"},{value:"German Level 0-A1",label:"German 0-A1"},
    {value:"German Level A1-A2",label:"German A1-A2"},{value:"German Level A2-B1",label:"German A2-B1"},
    {value:"German Level B1-B2",label:"German B1-B2"},{value:"Spanish Level 0-A1",label:"Spanish 0-A1"},
    {value:"Spanish Level A1-A2",label:"Spanish A1-A2"},{value:"Spanish Level A2-B1",label:"Spanish A2-B1"},
    {value:"French Level 0-A1",label:"French 0-A1"},{value:"French Level A1-A2",label:"French A1-A2"},
    {value:"French Level A2-B1",label:"French A2-B1"},
];
const CERT_FORMATS = [
    {value:"Անhатаkan",label:"Անhатаkan (Individual HY)"},{value:"Խмбаyin",label:"Խмбаyin (Group HY)"},
    {value:"Individual",label:"Individual (EN)"},{value:"Group",label:"Group (EN)"},
    {value:"Индивидуальный",label:"Индивидуальный (RU)"},{value:"Групповой",label:"Групповой (RU)"},
    {value:"ინდივიდუალური",label:"ინდივიდუალური (KA)"},{value:"ჯგუფური",label:"ჯგუფური (KA)"},
];
const CERT_PROGRAMS = [
    {value:"Քеmбриджyан",label:"Քеmбриджyан (HY)"},{value:"Cambridge",label:"Cambridge (EN)"},
    {value:"Кембриджская",label:"Кембриджская (RU)"},{value:"კემbrიжиs",label:"კემbrიжиs (KA)"},
];

function Certificates({ api, toast }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [viewModal, setViewModal] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => { const t = setTimeout(()=>setDebouncedSearch(search),400); return ()=>clearTimeout(t); }, [search]);
    const load = useCallback(async () => {
        setLoading(true);
        try { setData(await api("GET", `/certificates${debouncedSearch?`?q=${encodeURIComponent(debouncedSearch)}`:""}`)); }
        finally { setLoading(false); }
    }, [api, debouncedSearch]);
    useEffect(() => { load(); }, [load]);

    function setF(k, v) { setForm(p => ({ ...p, [k]: v })); }
    function openNew() { setForm({ issue_date: new Date().toISOString().split("T")[0], program: "Cambridge", course_format: "Individual" }); setModal(true); }
    function openEdit(c) { setForm({ ...c }); setModal(true); }

    async function save() {
        const required = ["full_name","certificate_code","course_name","issue_date","program","attendance","course_format"];
        const missing = required.filter(k => !form[k]?.toString().trim());
        if (missing.length) { toast(`Missing: ${missing.join(", ")}`, "err"); return; }
        setSaving(true);
        try {
            if (form.id) await api("PUT", `/certificates/${form.id}`, form);
            else await api("POST", "/certificates", form);
            toast(form.id?"Updated!":"Issued!", "ok"); setModal(null); load();
        } catch { toast("Save failed", "err"); } finally { setSaving(false); }
    }
    async function del(id, code) {
        if (!window.confirm(`Delete certificate ${code}?`)) return;
        await api("DELETE", `/certificates/${id}`); toast("Deleted","ok"); load();
    }
    function exportCSV() {
        const b = new Blob(["ID,Code,Name,Course,Level,Format,Attendance,Date\n"+data.map(c=>`${c.id},${c.certificate_code},"${c.full_name}","${c.course_name}","${c.level||""}","${c.course_format||""}",${c.attendance||""},${c.issue_date?String(c.issue_date).split("T")[0]:""}`).join("\n")], {type:"text/csv"});
        const a = document.createElement("a"); a.href=URL.createObjectURL(b); a.download="certificates.csv"; a.click();
    }

    const rows = data.map(c => ({
        __key: c.id,
        __cells: (
            <>
                <td><span className="adm-tag adm-tag-blue">{c.id}</span></td>
                <td><code className="adm-code">{c.certificate_code}</code></td>
                <td style={{fontWeight:500}}>{c.full_name}</td>
                <td>{c.course_name}</td>
                <td>{c.level?<span className="adm-tag adm-tag-yellow" style={{fontSize:11}}>{c.level}</span>:"—"}</td>
                <td>{c.course_format||"—"}</td>
                <td>{c.attendance||"—"}</td>
                <td>{c.issue_date?String(c.issue_date).split("T")[0]:"—"}</td>
                <td><div className="adm-actions">
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={()=>setViewModal(c)} title="Preview">👁</button>
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={()=>openEdit(c)}>✏️</button>
                    <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={()=>del(c.id,c.certificate_code)}>🗑</button>
                </div></td>
            </>
        ),
    }));

    return (
        <>
            <div className="adm-table-wrap">
                <div className="adm-table-toolbar">
                    <input className="adm-search" placeholder="Search by name, code, course…" value={search} onChange={e=>setSearch(e.target.value)}/>
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={exportCSV}>📥 CSV</button>
                    <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={openNew}>+ Issue Certificate</button>
                </div>
                {loading ? <div className="adm-loading"><span className="adm-spinner"/></div> : (
                    <div className="adm-table-scroll">
                        <table className="adm-data-table">
                            <thead><tr><th>ID</th><th>Code</th><th>Full Name</th><th>Course</th><th>Level</th><th>Format</th><th>Hours</th><th>Issue Date</th><th>Actions</th></tr></thead>
                            <tbody>{rows.length===0?<tr><td colSpan={9} className="adm-empty-cell">No certificates</td></tr>:rows.map((r,i)=><tr key={r.__key||i}>{r.__cells}</tr>)}</tbody>
                        </table>
                    </div>
                )}
            </div>

            {modal && (
                <Modal title={form.id?`Edit Certificate — ${form.certificate_code}`:"Issue New Certificate"} onClose={()=>setModal(null)} onSave={save} saving={saving} wide>
                    <div className="adm-form-grid">
                        <div className="adm-f-group adm-full"><label className="adm-f-label">Full Name <span className="adm-required">*</span></label><input className="adm-f-input" value={form.full_name||""} onChange={e=>setF("full_name",e.target.value)} autoFocus/></div>
                        <div className="adm-f-group"><label className="adm-f-label">Certificate Code <span className="adm-required">*</span></label><input className="adm-f-input adm-mono" value={form.certificate_code||""} onChange={e=>setF("certificate_code",e.target.value.toUpperCase())} placeholder="PA-2025-EN-001"/><span className="adm-f-hint">Must be unique</span></div>
                        <div className="adm-f-group"><label className="adm-f-label">Issue Date <span className="adm-required">*</span></label><input className="adm-f-input" type="date" value={form.issue_date?String(form.issue_date).split("T")[0]:""} onChange={e=>setF("issue_date",e.target.value)}/></div>
                        <div className="adm-f-group adm-full"><label className="adm-f-label">Course Name <span className="adm-required">*</span></label><input className="adm-f-input" value={form.course_name||""} onChange={e=>setF("course_name",e.target.value)}/></div>
                        <div className="adm-f-group adm-full"><label className="adm-f-label">Level <span className="adm-required">*</span></label>
                            <select className="adm-f-select" value={form.level||""} onChange={e=>setF("level",e.target.value)}>
                                <option value="">— Select —</option>
                                {CERT_LEVELS.map(l=><option key={l.value} value={l.value}>{l.label}</option>)}
                            </select>
                        </div>
                        <div className="adm-f-group"><label className="adm-f-label">Teaching Program <span className="adm-required">*</span></label>
                            <select className="adm-f-select" value={form.program||""} onChange={e=>setF("program",e.target.value)}>
                                <option value="">— Select —</option>
                                {CERT_PROGRAMS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                        </div>
                        <div className="adm-f-group"><label className="adm-f-label">Course Format <span className="adm-required">*</span></label>
                            <select className="adm-f-select" value={form.course_format||""} onChange={e=>setF("course_format",e.target.value)}>
                                <option value="">— Select —</option>
                                {CERT_FORMATS.map(f=><option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                        </div>
                        <div className="adm-f-group adm-full"><label className="adm-f-label">Attended Hours <span className="adm-required">*</span></label>
                            <input className="adm-f-input" value={form.attendance||""} onChange={e=>setF("attendance",e.target.value)} placeholder="e.g. 48"/>
                        </div>
                    </div>
                </Modal>
            )}

            {viewModal && (
                <Modal title={`Preview — ${viewModal.certificate_code}`} onClose={()=>setViewModal(null)} wide>
                    <div className="adm-cert-preview">
                        <div className="adm-cert-verified">✔ Certificate is Valid</div>
                        <section className="adm-cert-section">
                            <div className="adm-cert-section-title">🎓 Student Information</div>
                            <div className="adm-detail-grid">
                                <div className="adm-detail-row"><span>Full Name</span><strong>{viewModal.full_name}</strong></div>
                                <div className="adm-detail-row"><span>Course</span><strong>{viewModal.course_name}</strong></div>
                                <div className="adm-detail-row"><span>Level</span><strong>{viewModal.level||"—"}</strong></div>
                                <div className="adm-detail-row"><span>Format</span><strong>{viewModal.course_format||"—"}</strong></div>
                                <div className="adm-detail-row"><span>Program</span><strong>{viewModal.program||"—"}</strong></div>
                                <div className="adm-detail-row"><span>Attended Hours</span><strong>{viewModal.attendance||"—"}</strong></div>
                                <div className="adm-detail-row"><span>Issue Date</span><strong>{viewModal.issue_date?String(viewModal.issue_date).split("T")[0]:"—"}</strong></div>
                                <div className="adm-detail-row"><span>Code</span><code className="adm-code">{viewModal.certificate_code}</code></div>
                            </div>
                        </section>
                        <div style={{marginTop:14,display:"flex",gap:10}}>
                            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={()=>{setViewModal(null);openEdit(viewModal);}}>✏️ Edit</button>
                            <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={()=>{setViewModal(null);del(viewModal.id,viewModal.certificate_code);}}>🗑 Delete</button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: TELEGRAM VIP
// ═══════════════════════════════════════════════════════════════════
function TelegramSubs({ api, toast }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("active");
    const [extendModal, setExtendModal] = useState(null);
    const [days, setDays] = useState(30);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { setData(await api("GET", `/telegram-subs${statusFilter?`?status=${statusFilter}`:""}`)); } finally { setLoading(false); }
    }, [api, statusFilter]);
    useEffect(() => { load(); }, [load]);

    async function extend() {
        setSaving(true);
        try { await api("PUT", `/telegram-subs/${extendModal.id}/extend`, { days }); toast(`Extended by ${days} days!`,"ok"); setExtendModal(null); load(); }
        catch { toast("Failed","err"); } finally { setSaving(false); }
    }
    async function cancel(id) { if (!window.confirm("Cancel subscription?")) return; await api("PUT", `/telegram-subs/${id}/cancel`); toast("Cancelled","ok"); load(); }
    async function del(id) { if (!window.confirm("Delete record?")) return; await api("DELETE", `/telegram-subs/${id}`); toast("Deleted","ok"); load(); }

    const rows = data.map(s => {
        const left = daysUntil(s.expire_at);
        const expiring = left !== null && left <= 5 && left > 0;
        return {
            __key: s.id,
            __cells: (
                <>
                    <td><span className="adm-tag adm-tag-blue">{s.id}</span></td>
                    <td><div>{s.telegram_id}</div>{s.username&&<div style={{fontSize:11,color:"var(--adm-text2)"}}>@{s.username}</div>}</td>
                    <td><span className="adm-tag adm-tag-blue">{s.plan}</span></td>
                    <td><span style={{color:expiring?"var(--adm-yellow)":"inherit"}}>{fmtDate(s.expire_at)}</span>{expiring&&<span className="adm-tag adm-tag-yellow" style={{marginLeft:4,fontSize:10}}>⚠️{left}d</span>}</td>
                    <td><span className="adm-tag adm-tag-blue" style={{fontSize:10}}>{s.notified_3d?"✓3d":"—"} {s.notified_1d?"✓1d":"—"}</span></td>
                    <td><StatusBadge status={s.status}/></td>
                    <td><div className="adm-actions">
                        <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={()=>{setExtendModal(s);setDays(30);}}>➕</button>
                        {s.status==="active"&&<button className="adm-btn adm-btn-danger adm-btn-sm" onClick={()=>cancel(s.id)}>✕</button>}
                        <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={()=>del(s.id)}>🗑</button>
                    </div></td>
                </>
            ),
        };
    });

    return (
        <>
            <DataTable id="tgsubs" cols={["ID","Telegram","Plan","Expires","Notified","Status","Actions"]} rows={rows} loading={loading}
                statusFilter={statusFilter} onStatusFilter={setStatusFilter}
                statusOptions={[{value:"",label:"All"},{value:"active",label:"Active"},{value:"expired",label:"Expired"}]} />
            {extendModal && (
                <Modal title={`Extend #${extendModal.id}`} onClose={()=>setExtendModal(null)} onSave={extend} saving={saving}>
                    <div className="adm-detail-grid" style={{marginBottom:14}}>
                        <div className="adm-detail-row"><span>Telegram</span><strong>{extendModal.telegram_id}{extendModal.username&&` (@${extendModal.username})`}</strong></div>
                        <div className="adm-detail-row"><span>Current Expiry</span><strong>{fmtDate(extendModal.expire_at)}</strong></div>
                    </div>
                    <div className="adm-f-group">
                        <label className="adm-f-label">Extend by</label>
                        <select className="adm-f-select" value={days} onChange={e=>setDays(Number(e.target.value))}>
                            <option value={7}>7 days</option><option value={30}>30 days</option>
                            <option value={90}>90 days</option><option value={180}>180 days</option><option value={365}>365 days</option>
                        </select>
                    </div>
                    <div style={{marginTop:10,fontSize:13,color:"var(--adm-text2)"}}>New expiry: <strong>{(()=>{const b=new Date(extendModal.expire_at)>new Date()?new Date(extendModal.expire_at):new Date();b.setDate(b.getDate()+days);return fmtDate(b);})()}</strong></div>
                </Modal>
            )}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PANEL: SYSTEM HEALTH
// ═══════════════════════════════════════════════════════════════════
function SystemHealth({ api }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = useCallback(() => { setLoading(true); api("GET", "/health").then(d=>{setData(d);setLoading(false);}).catch(()=>setLoading(false)); }, [api]);
    useEffect(() => { load(); }, [load]);
    if (loading) return <div className="adm-loading"><span className="adm-spinner"/> Checking systems…</div>;
    if (!data) return <div className="adm-empty-cell">Health check failed</div>;
    const { checks } = data;
    function VarRow({ item }) {
        return <div className="adm-detail-row"><span className="adm-mono" style={{fontSize:12}}>{item.key}</span><span className={`adm-tag ${item.set?"adm-tag-green":"adm-tag-red"}`}>{item.set?"✓ Set":"✗ Missing"}</span></div>;
    }
    return (
        <div>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
                <h3 style={{color:"var(--adm-text)",fontSize:18}}>System Status</h3>
                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={load}>↺ Refresh</button>
                <span style={{fontSize:12,color:"var(--adm-text3)"}}>Checked: {fmtDate(data.timestamp)}</span>
            </div>
            <div className="adm-table-wrap" style={{marginBottom:14}}>
                <div className="adm-table-toolbar"><strong className="adm-section-title">🗄 Database</strong><span className={`adm-tag ${checks.database?.status==="ok"?"adm-tag-green":"adm-tag-red"}`}>{checks.database?.status==="ok"?"✓ Connected":"✗ Error"}</span></div>
            </div>
            <div className="adm-table-wrap" style={{marginBottom:14}}>
                <div className="adm-table-toolbar"><strong className="adm-section-title">💳 Payment Providers</strong></div>
                <div style={{padding:"0 16px 16px"}}>
                    {Object.entries(checks.payments||{}).map(([name,info])=>(
                        <div key={name} style={{marginBottom:14}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                                <strong style={{color:"var(--adm-text)",fontSize:13}}>{name.toUpperCase()}</strong>
                                <span className={`adm-tag ${info.status==="configured"?"adm-tag-green":"adm-tag-red"}`}>{info.status}</span>
                            </div>
                            {info.vars?.map(v=><VarRow key={v.key} item={v}/>)}
                        </div>
                    ))}
                </div>
            </div>
            <div className="adm-table-wrap" style={{marginBottom:14}}>
                <div className="adm-table-toolbar"><strong className="adm-section-title">🔗 Integrations</strong></div>
                <div style={{padding:"0 16px 16px"}}>
                    {Object.values(checks.integrations||{}).map(item=><VarRow key={item.key} item={item}/>)}
                </div>
            </div>
            {checks.cron && (
                <div className="adm-table-wrap">
                    <div className="adm-table-toolbar"><strong className="adm-section-title">⏰ Cron Jobs</strong></div>
                    <div style={{padding:"8px 16px 16px"}}><div className="adm-detail-row"><span>Last expiry processed</span><strong>{checks.cron.last_expiry_processed?fmtDate(checks.cron.last_expiry_processed):"Never"}</strong></div></div>
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// NAV CONFIG
// ═══════════════════════════════════════════════════════════════════
const NAV = [
    { id: "dashboard",    icon: "📊", label: "Dashboard",          section: "Overview" },
    { id: "products",     icon: "🌐", label: "Products / Courses",  section: "Content" },
    { id: "comments",     icon: "💬", label: "Reviews",             section: null },
    { id: "teachers",     icon: "👨‍🏫", label: "Teachers",           section: null },
    { id: "team",         icon: "👥", label: "Our Team",            section: null },
    { id: "gallery",      icon: "🖼",  label: "Gallery",             section: null },
    { id: "partners",     icon: "🤝", label: "Partners",            section: null },
    { id: "certificates", icon: "🎓", label: "Certificates",        section: "Academy" },
    { id: "telegram",     icon: "📱", label: "Telegram VIP",        section: null },
    { id: "translations", icon: "🌍", label: "Languages & Text",    section: "CMS" },
    { id: "settings",     icon: "⚙️", label: "Site Settings",       section: null },
    { id: "seo",          icon: "🔍", label: "SEO Management",      section: null },
    { id: "orders",       icon: "🛒", label: "Orders",              section: "Business" },
    { id: "newsletter",   icon: "📧", label: "Subscribers",         section: null },
    { id: "contacts",     icon: "📩", label: "Messages",            section: null },
    { id: "health",       icon: "🔧", label: "System Health",       section: "Operations" },
];

const PANEL_TITLES = {
    dashboard:"Dashboard", products:"Products / Courses", comments:"Reviews",
    teachers:"Teachers", team:"Our Team", gallery:"Gallery", partners:"Partners",
    certificates:"Certificates", telegram:"Telegram VIP Subscriptions",
    translations:"Languages & Translations", settings:"Site Settings (CMS)",
    seo:"SEO Management", orders:"Orders", newsletter:"Newsletter Subscribers",
    contacts:"Contact Messages", health:"System Health",
};

// ═══════════════════════════════════════════════════════════════════
// ROOT COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function AdminPage() {
    const [token, setToken]       = useState(() => localStorage.getItem("adm_token") || "");
    const [verified, setVerified] = useState(false);
    const [checking, setChecking] = useState(true);
    const [panel, setPanel]       = useState("dashboard");
    const [loginForm, setLoginForm] = useState({ username: "", password: "" });
    const [loginErr, setLoginErr]   = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const { toasts, push: toast }   = useToast();
    const api = useApi(token);

    useEffect(() => {
        if (!token) { setChecking(false); return; }
        fetch(API_BASE + "/verify", { headers: { "x-admin-token": token } })
            .then(r => r.json())
            .then(j => { if (j.valid) setVerified(true); else { setToken(""); localStorage.removeItem("adm_token"); } })
            .catch(() => { setToken(""); localStorage.removeItem("adm_token"); })
            .finally(() => setChecking(false));
    }, []); // eslint-disable-line

    async function login() {
        setLoggingIn(true); setLoginErr("");
        try {
            const r = await fetch(API_BASE + "/login", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify(loginForm),
            });
            const j = await r.json();
            if (r.ok && j.token) {
                localStorage.setItem("adm_token", j.token);
                setToken(j.token); setVerified(true);
            } else { setLoginErr(j.error || "Invalid credentials"); }
        } catch { setLoginErr("Cannot reach server."); } finally { setLoggingIn(false); }
    }

    function logout() {
        localStorage.removeItem("adm_token");
        setToken(""); setVerified(false);
    }

    const handleApi = useCallback(async (method, path, body, isFormData) => {
        try { return await api(method, path, body, isFormData); }
        catch (e) { if (e.message === "__UNAUTHORIZED__") logout(); throw e; }
    }, [api]); // eslint-disable-line

    if (checking) return <div className="adm-full-center"><span className="adm-spinner adm-spinner-lg" /></div>;

    if (!verified) return (
        <div className="adm-full-center adm-login-bg">
            <div className="adm-login-card">
                <div className="adm-login-logo">Polyglot <span>Academy</span></div>
                <div className="adm-login-sub">Admin Panel</div>
                <div className="adm-field"><label>Username</label>
                    <input type="text" value={loginForm.username} placeholder="admin" autoComplete="username"
                        onChange={e => setLoginForm(p => ({ ...p, username: e.target.value }))}
                        onKeyDown={e => e.key === "Enter" && login()} /></div>
                <div className="adm-field"><label>Password</label>
                    <input type="password" value={loginForm.password} placeholder="••••••••" autoComplete="current-password"
                        onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
                        onKeyDown={e => e.key === "Enter" && login()} /></div>
                <button className="adm-btn adm-btn-primary adm-btn-full" onClick={login} disabled={loggingIn}>
                    {loggingIn ? <span className="adm-spinner" /> : "Sign In"}
                </button>
                {loginErr && <div className="adm-login-err">{loginErr}</div>}
            </div>
            <ToastContainer toasts={toasts} />
        </div>
    );

    const panelMap = {
        dashboard:    <Dashboard    api={handleApi} />,
        products:     <Products     api={handleApi} token={token} toast={toast} />,
        comments:     <Comments     api={handleApi} token={token} toast={toast} />,
        teachers:     <Teachers     api={handleApi} token={token} toast={toast} />,
        team:         <Team         api={handleApi} token={token} toast={toast} />,
        gallery:      <Gallery      api={handleApi} token={token} toast={toast} />,
        partners:     <Partners     api={handleApi} token={token} toast={toast} />,
        certificates: <Certificates api={handleApi} toast={toast} />,
        telegram:     <TelegramSubs api={handleApi} toast={toast} />,
        translations: <Translations api={handleApi} toast={toast} />,
        settings:     <SiteSettings api={handleApi} token={token} toast={toast} />,
        seo:          <SEOManagement api={handleApi} toast={toast} />,
        orders:       <Orders       api={handleApi} toast={toast} />,
        newsletter:   <Newsletter   api={handleApi} toast={toast} />,
        contacts:     <Contacts     api={handleApi} toast={toast} />,
        health:       <SystemHealth api={handleApi} />,
    };

    let lastSection = null;

    return (
        <div className="adm-layout">
            <aside className="adm-sidebar">
                <div className="adm-sidebar-head">
                    <div className="adm-sidebar-logo">Polyglot <span>Admin</span></div>
                    <div className="adm-sidebar-badge">Control Panel</div>
                </div>
                <nav className="adm-sidebar-nav">
                    {NAV.map(item => {
                        const showSection = item.section && item.section !== lastSection;
                        if (item.section) lastSection = item.section;
                        return (
                            <React.Fragment key={item.id}>
                                {showSection && <div className="adm-nav-section">{item.section}</div>}
                                <div className={`adm-nav-item${panel === item.id ? " active" : ""}`}
                                    onClick={() => setPanel(item.id)}>
                                    <span className="adm-nav-icon">{item.icon}</span>
                                    {item.label}
                                </div>
                            </React.Fragment>
                        );
                    })}
                </nav>
                <div className="adm-sidebar-foot">
                    <button className="adm-btn adm-btn-ghost adm-btn-full adm-btn-sm" onClick={logout}>⬅ Sign Out</button>
                </div>
            </aside>

            <div className="adm-main">
                <div className="adm-topbar">
                    <div className="adm-topbar-title">{PANEL_TITLES[panel]}</div>
                    <div className="adm-topbar-right">
                        <div className="adm-user-chip">
                            <div className="adm-avatar">A</div>
                            <span>Admin</span>
                        </div>
                    </div>
                </div>
                <div className="adm-content">{panelMap[panel]}</div>
            </div>

            <ToastContainer toasts={toasts} />
        </div>
    );
}
